import { computed, inject, Injectable, signal } from '@angular/core';
import { Store, StoreListResponse } from '@api/models/dashboard';
import { STORES_PAGE_SIZE } from '@shared/constants';
import { StoresData } from '@shared/services/dashboard';
import { LogsData } from '@shared/services/logs';
import { catchError, EMPTY, finalize, Observable, tap } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class StoreSelectionStore {
  private readonly storesData = inject(StoresData);
  private readonly logs = inject(LogsData);

  // State signals
  private readonly _stores = signal<Store[]>([]);
  private readonly _switcherStores = signal<Store[]>([]);
  private readonly _selectedStoreId = signal<string | null>(null);
  private readonly _isLoading = signal(false);
  private readonly _totalCount = signal(0);
  private readonly _initialTotalCount = signal(0);
  private readonly _currentPage = signal(1);
  private readonly _totalPages = signal(1);
  private readonly _defaultStoreId = signal<string | null>(null);

  // Public readonly
  readonly stores = this._stores.asReadonly();
  readonly switcherStores = this._switcherStores.asReadonly();
  readonly selectedStoreId = this._selectedStoreId.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();
  readonly totalCount = this._totalCount.asReadonly();
  readonly currentPage = this._currentPage.asReadonly();
  readonly totalPages = this._totalPages.asReadonly();
  readonly defaultStoreId = this._defaultStoreId.asReadonly();

  // Computed — hasMultipleStores/hasManyStores use initial count (unfiltered) to avoid layout switching on search
  readonly selectedStore = computed(() => {
    const id = this._selectedStoreId();
    return this._stores().find((s) => s.id === id) ?? this._switcherStores().find((s) => s.id === id) ?? null;
  });
  readonly hasMultipleStores = computed(() => this._initialTotalCount() > 1);
  readonly hasManyStores = computed(() => this._initialTotalCount() > 5);

  loadStores(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    filterBrand?: string;
    filterType?: string;
  }): Observable<StoreListResponse> {
    this._isLoading.set(true);
    return this.storesData.fetchStores(params).pipe(
      tap((response) => {
        this._stores.set(response.stores);
        this._totalCount.set(response.totalCount);
        this._currentPage.set(response.page);
        this._totalPages.set(response.totalPages);
        if (this._initialTotalCount() === 0) {
          this._initialTotalCount.set(response.totalCount);
          this._switcherStores.set(response.stores);
        }
        if (response.defaultStoreId) {
          this._defaultStoreId.set(response.defaultStoreId);
        }
        this.logs.info('💽 StoreSelectionStore', 'Stores loaded', response);
      }),
      catchError((error) => {
        this.logs.error('💽 StoreSelectionStore', 'Failed to load stores', error);
        return EMPTY;
      }),
      finalize(() => this._isLoading.set(false))
    );
  }

  loadSwitcherStores(search?: string): void {
    this.storesData
      .fetchStores({ page: 1, pageSize: STORES_PAGE_SIZE, search: search || undefined })
      .pipe(
        tap((response) => {
          this._switcherStores.set(response.stores);
          this.logs.info('💽 StoreSelectionStore', 'Switcher stores loaded', response);
        }),
        catchError((error) => {
          this.logs.error('💽 StoreSelectionStore', 'Failed to load switcher stores', error);
          return EMPTY;
        })
      )
      .subscribe();
  }

  selectStore(storeId: string): void {
    this._selectedStoreId.set(storeId);
    this.logs.info('💽 StoreSelectionStore', 'Store selected', storeId);
  }
}
